import React, { useState } from "react";
import {
    Search,
    FileText,
    CheckCircle2,
    Package,
    Truck,
    Clock,
    ChevronRight,
    X,
    MapPin,
    Calendar,
    IndianRupee,
    Building2,
    Star,
} from "lucide-react";

// ---- Sample data (swap for real API data) ----------------------------------
const initialOrders = [
    {
        id: "APIC-ORD-00081",
        enquiryId: "APIC-ENQ-00123",
        item: "Smart IoT Water Sensor",
        category: "IoT / PCB",
        vendor: "Veltrix Electronics Pvt Ltd",
        vendorRating: 4.8,
        quotedAmount: 178000,
        qty: 250,
        unit: "units",
        deliveryDate: "2026-08-30",
        location: "Pune, MH",
        status: "pending_acceptance",
    },
    {
        id: "APIC-ORD-00080",
        enquiryId: "APIC-ENQ-00119",
        item: "Precision Steel Brackets",
        category: "Industrial",
        vendor: "Sharma Fabrication Works",
        vendorRating: 4.5,
        quotedAmount: 62000,
        qty: 600,
        unit: "pcs",
        deliveryDate: "2026-08-10",
        location: "Ludhiana, PB",
        status: "accepted",
    },
    {
        id: "APIC-ORD-00079",
        enquiryId: "APIC-ENQ-00115",
        item: "ABS Enclosure Set (Grey)",
        category: "3D Printing",
        vendor: "PrintForm Industries",
        vendorRating: 4.2,
        quotedAmount: 41500,
        qty: 300,
        unit: "units",
        deliveryDate: "2026-07-28",
        location: "Coimbatore, TN",
        status: "in_production",
    },
    {
        id: "APIC-ORD-00076",
        enquiryId: "APIC-ENQ-00108",
        item: "Custom Wiring Harness",
        category: "Electrical",
        vendor: "Anand Cable Solutions",
        vendorRating: 4.6,
        quotedAmount: 95000,
        qty: 150,
        unit: "sets",
        deliveryDate: "2026-07-18",
        location: "Nashik, MH",
        status: "shipped",
    },
    {
        id: "APIC-ORD-00071",
        enquiryId: "APIC-ENQ-00099",
        item: "Aluminium Heat Sinks",
        category: "Industrial",
        vendor: "Metro Metal Works",
        vendorRating: 4.4,
        quotedAmount: 53200,
        qty: 400,
        unit: "pcs",
        deliveryDate: "2026-06-30",
        location: "Aurangabad, MH",
        status: "delivered",
        deliveredDate: "2026-06-28",
        invoiceId: "INV-2026-0071",
    },
    {
        id: "APIC-ORD-00065",
        enquiryId: "APIC-ENQ-00090",
        item: "PCB Assembly Batch (2-layer)",
        category: "IoT / PCB",
        vendor: "Veltrix Electronics Pvt Ltd",
        vendorRating: 4.8,
        quotedAmount: 132500,
        qty: 500,
        unit: "units",
        deliveryDate: "2026-06-10",
        location: "Pune, MH",
        status: "delivered",
        deliveredDate: "2026-06-08",
        invoiceId: "INV-2026-0065",
    },
    {
        id: "APIC-ORD-00058",
        enquiryId: "APIC-ENQ-00082",
        item: "Injection Moulded Enclosures",
        category: "3D Printing",
        vendor: "PrintForm Industries",
        vendorRating: 4.2,
        quotedAmount: 38900,
        qty: 350,
        unit: "units",
        deliveryDate: "2026-05-22",
        location: "Coimbatore, TN",
        status: "delivered",
        deliveredDate: "2026-05-20",
        invoiceId: "INV-2026-0058",
    },
];

// ---- Status configuration ---------------------------------------------------
const STATUS_CONFIG = {
    pending_acceptance: {
        label: "Awaiting Your Acceptance",
        dot: "#F59E0B",
        bg: "#FFF7E6",
        text: "#B45309",
    },
    accepted: {
        label: "Order Placed",
        dot: "#6366F1",
        bg: "#EEF0FF",
        text: "#4338CA",
    },
    in_production: {
        label: "In Production",
        dot: "#0EA5E9",
        bg: "#E9F7FE",
        text: "#0369A1",
    },
    shipped: {
        label: "Shipped",
        dot: "#8B5CF6",
        bg: "#F3EEFE",
        text: "#6D28D9",
    },
    delivered: {
        label: "Delivered",
        dot: "#22C55E",
        bg: "#EAFBF1",
        text: "#15803D",
    },
};

// ---- Custom scrollbar styling ------------------------------------------------
function ScrollbarStyles() {
    return (
        <style>{`
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: #CBD5E1;
        border-radius: 9999px;
        border: 2px solid transparent;
        background-clip: padding-box;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background-color: #94A3B8;
      }
      .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: #CBD5E1 transparent;
      }
    `}</style>
    );
}

const currency = (n) =>
    "\u20B9" + n.toLocaleString("en-IN");

const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

// ---- Stat card ---------------------------------------------------------------
function StatCard({ label, value, icon: Icon, accent }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
                    {label}
                </span>
                <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: accent + "15" }}
                >
                    <Icon size={16} style={{ color: accent }} />
                </div>
            </div>
            <span className="text-2xl font-bold text-slate-900">{value}</span>
        </div>
    );
}

// ---- Confirm & place order modal ---------------------------------------------
function AcceptOrderModal({ order, onClose, onConfirm }) {
    const [address, setAddress] = useState("");
    const [note, setNote] = useState("");
    const canSubmit = address.trim().length > 3;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
                onClick={onClose}
            />
            <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-slate-100">
                    <div>
                        <h2 className="text-lg font-bold text-slate-900">
                            Accept quotation & place order
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">
                            {order.enquiryId} &middot; {order.item}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 rounded-lg p-1"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-3">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500 flex items-center gap-1.5">
                                <Building2 size={14} /> Vendor
                            </span>
                            <span className="font-medium text-slate-800">{order.vendor}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500 flex items-center gap-1.5">
                                <IndianRupee size={14} /> Quoted amount
                            </span>
                            <span className="font-semibold text-slate-900">
                                {currency(order.quotedAmount)}
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500 flex items-center gap-1.5">
                                <Calendar size={14} /> Delivery by
                            </span>
                            <span className="font-medium text-slate-800">
                                {formatDate(order.deliveryDate)}
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500 flex items-center gap-1.5">
                                <Package size={14} /> Quantity
                            </span>
                            <span className="font-medium text-slate-800">
                                {order.qty} {order.unit}
                            </span>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">
                            Delivery address
                        </label>
                        <textarea
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            rows={2}
                            placeholder="Enter the address this order should be delivered to"
                            className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1.5">
                            Note to vendor <span className="text-slate-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            placeholder="Any instructions for packaging, labeling, or handling"
                            className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none"
                        />
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 bg-slate-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={!canSubmit}
                        onClick={() => onConfirm(order.id)}
                        className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-1.5"
                    >
                        Confirm & place order <ChevronRight size={15} />
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- Invoice modal ---------------------------------------------------------
function InvoiceModal({ order, onClose }) {
    if (!order) return null;

    const unitPrice = order.quotedAmount / order.qty;
    const taxRate = 0.18;
    const taxAmount = order.quotedAmount * taxRate;
    const grandTotal = order.quotedAmount + taxAmount;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
                onClick={onClose}
            />
            <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-slate-100">
                    <div>
                        <h2 className="text-lg font-bold text-slate-900">Invoice</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            {order.invoiceId} &middot; {order.id}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 rounded-lg p-1"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto custom-scrollbar">
                    {/* Status + dates */}
                    <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                            <CheckCircle2 size={12} /> Delivered {formatDate(order.deliveredDate)}
                        </span>
                        <span className="text-xs text-slate-400">
                            Order placed &middot; {order.enquiryId}
                        </span>
                    </div>

                    {/* Vendor / delivery details */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                            <div className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase mb-2">
                                Billed by
                            </div>
                            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                                <Building2 size={14} /> {order.vendor}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                                <MapPin size={12} /> {order.location}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                                <Star size={11} className="fill-amber-400 text-amber-400" />
                                {order.vendorRating} vendor rating
                            </div>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                            <div className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase mb-2">
                                Delivery details
                            </div>
                            <div className="flex items-center gap-1.5 text-sm text-slate-700">
                                <Calendar size={14} /> Delivered {formatDate(order.deliveredDate)}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                                <Package size={12} /> {order.qty} {order.unit} received
                            </div>
                        </div>
                    </div>

                    {/* Line items */}
                    <div>
                        <div className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase mb-2">
                            Line items
                        </div>
                        <div className="rounded-xl border border-slate-100 overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50 text-left text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
                                        <th className="px-4 py-2.5 font-semibold">Item</th>
                                        <th className="px-4 py-2.5 font-semibold text-right">Qty</th>
                                        <th className="px-4 py-2.5 font-semibold text-right">Unit Price</th>
                                        <th className="px-4 py-2.5 font-semibold text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-800">{order.item}</div>
                                            <div className="text-xs text-slate-400">{order.category}</div>
                                        </td>
                                        <td className="px-4 py-3 text-right text-slate-600">
                                            {order.qty} {order.unit}
                                        </td>
                                        <td className="px-4 py-3 text-right text-slate-600">
                                            {currency(Math.round(unitPrice))}
                                        </td>
                                        <td className="px-4 py-3 text-right font-medium text-slate-800">
                                            {currency(order.quotedAmount)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Totals */}
                    <div className="space-y-2 border-t border-slate-100 pt-4">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">Subtotal</span>
                            <span className="text-slate-700">{currency(order.quotedAmount)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">GST (18%)</span>
                            <span className="text-slate-700">{currency(Math.round(taxAmount))}</span>
                        </div>
                        <div className="flex items-center justify-between text-base font-bold pt-2 border-t border-slate-100">
                            <span className="text-slate-900">Total paid</span>
                            <span className="text-slate-900">{currency(Math.round(grandTotal))}</span>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 bg-slate-50">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100"
                    >
                        Close
                    </button>
                    <button className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1.5">
                        <FileText size={15} /> Download PDF
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- Main page -----------------------------------------------------------------
export default function OrdersPage() {
    const [orders, setOrders] = useState(initialOrders);
    const [query, setQuery] = useState("");
    const [activeOrder, setActiveOrder] = useState(null);
    const [invoiceOrder, setInvoiceOrder] = useState(null);
    const [toast, setToast] = useState(null);
    const [tab, setTab] = useState("active"); // "active" | "delivered"

    const counts = {
        total: orders.length,
        pending: orders.filter((o) => o.status === "pending_acceptance").length,
        accepted: orders.filter((o) =>
            ["accepted", "in_production", "shipped"].includes(o.status)
        ).length,
        delivered: orders.filter((o) => o.status === "delivered").length,
    };

    const matchesQuery = (o) => {
        const q = query.toLowerCase();
        return (
            o.item.toLowerCase().includes(q) ||
            o.id.toLowerCase().includes(q) ||
            o.vendor.toLowerCase().includes(q) ||
            o.category.toLowerCase().includes(q)
        );
    };

    // Active tab = quotation accepted, preparing, or on the way (not yet delivered)
    const activeOrders = orders.filter(
        (o) =>
            ["pending_acceptance", "accepted", "in_production", "shipped"].includes(
                o.status
            ) && matchesQuery(o)
    );

    // Delivered tab = orders that have already reached the customer
    const deliveredOrders = orders.filter(
        (o) => o.status === "delivered" && matchesQuery(o)
    );

    const filtered = tab === "active" ? activeOrders : deliveredOrders;

    const handleConfirm = (orderId) => {
        setOrders((prev) =>
            prev.map((o) =>
                o.id === orderId ? { ...o, status: "accepted" } : o
            )
        );
        setActiveOrder(null);
        setToast("Order placed. The vendor has been notified.");
        setTimeout(() => setToast(null), 3200);
    };

    return (
        <div className="h-screen overflow-y-auto custom-scrollbar bg-slate-50 py-8 px-6 font-sans">
            <ScrollbarStyles />
            <div className="max-w-6xl mx-auto space-y-6">
                <div>
                    <h1 className="text-xl font-bold text-slate-900">Orders</h1>
                    <p className="text-sm text-slate-500 mt-0.5">
                        Review accepted quotations and track every order through to delivery.
                    </p>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Total Orders" value={counts.total} icon={Package} accent="#6366F1" />
                    <StatCard
                        label="Pending Acceptance"
                        value={counts.pending}
                        icon={Clock}
                        accent="#F59E0B"
                    />
                    <StatCard
                        label="Accepted / Active"
                        value={counts.accepted}
                        icon={CheckCircle2}
                        accent="#0EA5E9"
                    />
                    <StatCard
                        label="Delivered"
                        value={counts.delivered}
                        icon={Truck}
                        accent="#22C55E"
                    />
                </div>

                {/* Search */}
                <div className="relative max-w-md">
                    <Search
                        size={16}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by order ID, item, vendor..."
                        className="w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    />
                </div>

                {/* Tabs */}
                <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
                    <button
                        onClick={() => setTab("active")}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "active"
                            ? "bg-white text-indigo-700 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                            }`}
                    >
                        Active Orders
                        <span
                            className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-[11px] font-bold ${tab === "active"
                                ? "bg-indigo-100 text-indigo-700"
                                : "bg-slate-200 text-slate-500"
                                }`}
                        >
                            {counts.pending + counts.accepted}
                        </span>
                    </button>
                    <button
                        onClick={() => setTab("delivered")}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === "delivered"
                            ? "bg-white text-indigo-700 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                            }`}
                    >
                        Delivered Orders
                        <span
                            className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-[11px] font-bold ${tab === "delivered"
                                ? "bg-indigo-100 text-indigo-700"
                                : "bg-slate-200 text-slate-500"
                                }`}
                        >
                            {counts.delivered}
                        </span>
                    </button>
                </div>

                {/* Orders table */}
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h2 className="font-bold text-slate-900 text-[15px]">
                            {tab === "active" ? "Active Orders" : "Delivered Orders"}
                        </h2>
                        <p className="text-xs text-slate-400 mt-0.5">
                            {tab === "active"
                                ? "Accept new quotations and track orders being prepared or shipped."
                                : "A record of every order that has reached your customer."}
                        </p>
                    </div>

                    <div className="overflow-auto max-h-[480px] custom-scrollbar">
                        <table className="w-full text-sm">
                            {tab === "active" ? (
                                <>
                                    <thead className="sticky top-0 z-10 bg-white">
                                        <tr className="text-left text-[11px] font-semibold tracking-wider text-slate-400 uppercase border-b border-slate-100">
                                            <th className="px-6 py-3 font-semibold">Order</th>
                                            <th className="px-4 py-3 font-semibold">Vendor</th>
                                            <th className="px-4 py-3 font-semibold">Quoted Amount</th>
                                            <th className="px-4 py-3 font-semibold">Delivery Target</th>
                                            <th className="px-4 py-3 font-semibold">Status</th>
                                            <th className="px-6 py-3 font-semibold text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((o) => {
                                            const cfg = STATUS_CONFIG[o.status];
                                            return (
                                                <tr
                                                    key={o.id}
                                                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors"
                                                >
                                                    <td className="px-6 py-4">
                                                        <div className="font-semibold text-slate-900">{o.item}</div>
                                                        <div className="text-xs text-slate-400 mt-0.5">
                                                            {o.id} &middot; {o.category}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="font-medium text-slate-700">{o.vendor}</div>
                                                        <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
                                                            <Star size={11} className="fill-amber-400 text-amber-400" />
                                                            {o.vendorRating}
                                                            <span className="mx-1">&middot;</span>
                                                            <MapPin size={11} /> {o.location}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="font-semibold text-slate-900">
                                                            {currency(o.quotedAmount)}
                                                        </div>
                                                        <div className="text-xs text-slate-400 mt-0.5">
                                                            {o.qty} {o.unit}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 text-slate-600">
                                                        {formatDate(o.deliveryDate)}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span
                                                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                                                            style={{ backgroundColor: cfg.bg, color: cfg.text }}
                                                        >
                                                            <span
                                                                className="w-1.5 h-1.5 rounded-full"
                                                                style={{ backgroundColor: cfg.dot }}
                                                            />
                                                            {cfg.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        {o.status === "pending_acceptance" ? (
                                                            <button
                                                                onClick={() => setActiveOrder(o)}
                                                                className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-xl"
                                                            >
                                                                Accept & Place Order <ChevronRight size={14} />
                                                            </button>
                                                        ) : (
                                                            <button className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm font-medium px-3 py-2 rounded-xl border border-slate-200 hover:bg-slate-50">
                                                                <FileText size={14} /> View Details
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {filtered.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="px-6 py-10 text-center text-slate-400 text-sm">
                                                    No active orders match your search.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </>
                            ) : (
                                <>
                                    <thead className="sticky top-0 z-10 bg-white">
                                        <tr className="text-left text-[11px] font-semibold tracking-wider text-slate-400 uppercase border-b border-slate-100">
                                            <th className="px-6 py-3 font-semibold">Order</th>
                                            <th className="px-4 py-3 font-semibold">Vendor</th>
                                            <th className="px-4 py-3 font-semibold">Amount Paid</th>
                                            <th className="px-4 py-3 font-semibold">Delivered On</th>
                                            <th className="px-4 py-3 font-semibold">Invoice</th>
                                            <th className="px-6 py-3 font-semibold text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((o) => (
                                            <tr
                                                key={o.id}
                                                className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="font-semibold text-slate-900">{o.item}</div>
                                                    <div className="text-xs text-slate-400 mt-0.5">
                                                        {o.id} &middot; {o.category}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="font-medium text-slate-700">{o.vendor}</div>
                                                    <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
                                                        <Star size={11} className="fill-amber-400 text-amber-400" />
                                                        {o.vendorRating}
                                                        <span className="mx-1">&middot;</span>
                                                        <MapPin size={11} /> {o.location}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="font-semibold text-slate-900">
                                                        {currency(o.quotedAmount)}
                                                    </div>
                                                    <div className="text-xs text-slate-400 mt-0.5">
                                                        {o.qty} {o.unit}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                                                        <CheckCircle2 size={12} />
                                                        {formatDate(o.deliveredDate)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 text-slate-600">{o.invoiceId}</td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="inline-flex items-center gap-2">
                                                        <button
                                                            onClick={() => setInvoiceOrder(o)}
                                                            className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm font-medium px-3 py-2 rounded-xl border border-slate-200 hover:bg-slate-50"
                                                        >
                                                            <FileText size={14} /> Invoice
                                                        </button>
                                                        <button className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 text-sm font-medium px-3 py-2 rounded-xl border border-indigo-100 hover:bg-indigo-50">
                                                            <Star size={14} /> Rate Vendor
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {filtered.length === 0 && (
                                            <tr>
                                                <td colSpan={6} className="px-6 py-10 text-center text-slate-400 text-sm">
                                                    No delivered orders match your search.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </>
                            )}
                        </table>
                    </div>
                </div>
            </div>

            {activeOrder && (
                <AcceptOrderModal
                    order={activeOrder}
                    onClose={() => setActiveOrder(null)}
                    onConfirm={handleConfirm}
                />
            )}

            {invoiceOrder && (
                <InvoiceModal order={invoiceOrder} onClose={() => setInvoiceOrder(null)} />
            )}

            {toast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 z-50">
                    <CheckCircle2 size={16} className="text-emerald-400" />
                    {toast}
                </div>
            )}
        </div>
    );
}